// src/features/auth/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { loginAPI, verifySignUP, googleAuthExchange, appleAuthExchange } from '../../api/loginApi'
import { removeCookie } from 'shared-ui/utils/cookiesHelper'
import { unifiedJobStreamService } from '../../services/unifiedJobStreamService'

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, calling_code, mobile }, thunkAPI) => {
    try {
      const response = await loginAPI(email, password, calling_code, mobile)
      return response
    } catch (error) {
      let errorMessage = 'Something went wrong'
      if (typeof error?.message === 'string') {
        try {
          const parsed = JSON.parse(error.message)
          if (parsed?.message) errorMessage = parsed.message
        } catch (e) {
          errorMessage = error.message
        }
      }

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      }

      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const register = createAsyncThunk(
  'auth/register',
  async (formData, thunkAPI) => {
    try {
      const response = await verifySignUP(formData)
      return response
    } catch (error) {
      let message =
        typeof error === 'string'
          ? error
          : error?.detail || error?.message || JSON.stringify(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (idToken, thunkAPI) => {
    try {
      const response = await googleAuthExchange(idToken)
      return response
    } catch (error) {
      let message =
        typeof error === 'string'
          ? error
          : error?.detail || error?.message || JSON.stringify(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const appleLogin = createAsyncThunk(
  'auth/appleLogin',
  async ({ idToken, grantCode }, thunkAPI) => {
    try {
      const response = await appleAuthExchange(idToken, grantCode)
      return response
    } catch (error) {
      let message =
        typeof error === 'string'
          ? error
          : error?.detail || error?.message || JSON.stringify(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    status: 'idle',
    error: null,
    isAuthenticated: !!localStorage.getItem('access'),
    fcmToken:null
  },
  reducers: {
    clearError(state) {
      state.error = null
    },
    logout(state) {
      state.user = null
      state.isAuthenticated = false
      localStorage.removeItem('user')
      localStorage.removeItem('access')
      localStorage.removeItem('mini_app_consents')
      localStorage.removeItem('isSocialUser')
      removeCookie('auth_token')
      try {
        localStorage.removeItem('activeJobs')
        localStorage.removeItem('active_jobs_persist_v1')
      } catch (e) {}
      try {
        unifiedJobStreamService.disconnect()
      } catch (e) {}
    },
    updateFcmToken: (state, action) => {
      state.fcmToken = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // LOGIN
      .addCase(login.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.user =
          action.payload.data?.user || action.payload.data || action.payload.user || action.payload
        state.isAuthenticated = true
        localStorage.setItem('user', JSON.stringify(state.user))
        const token = action.payload.data?.token || action.payload.data?.auth_token || action.payload.access || action.payload.token
        if (token) {
          localStorage.setItem('access', token)
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
        state.isAuthenticated = false
      })

      // REGISTER
      .addCase(register.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(register.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.user =
          action.payload.data?.user || action.payload.data || action.payload.user || action.payload
        state.isAuthenticated = true
        localStorage.setItem('user', JSON.stringify(state.user))
        const token = action.payload.data?.token || action.payload.data?.auth_token || action.payload.access || action.payload.token
        if (token) {
          localStorage.setItem('access', token)
        }
      })
      //googlelogin
      .addCase(register.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
        state.isAuthenticated = false
      })

      .addCase(googleLogin.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.user =
          action.payload.data?.user || action.payload.data || action.payload.user || action.payload
        state.isAuthenticated = true
        localStorage.setItem('user', JSON.stringify(state.user))
        localStorage.removeItem('isGuest')
        const token =
          action.payload.data?.token ||
          action.payload.data?.auth_token ||
          action.payload.data?.access ||
          action.payload.access ||
          action.payload.token
        if (token) {
          localStorage.setItem('access', token)
        }
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
        state.isAuthenticated = false
      })
      .addCase(appleLogin.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(appleLogin.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.user =
          action.payload.data?.user || action.payload.data || action.payload.user || action.payload
        state.isAuthenticated = true
        localStorage.setItem('user', JSON.stringify(state.user))
        localStorage.removeItem('isGuest')
        const token =
          action.payload.data?.token ||
          action.payload.data?.auth_token ||
          action.payload.data?.access ||
          action.payload.access ||
          action.payload.token
        if (token) {
          localStorage.setItem('access', token)
        }
      })
      .addCase(appleLogin.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
        state.isAuthenticated = false
      })
  }
})

export const { clearError, logout,updateFcmToken } = authSlice.actions

export default authSlice.reducer